import type { LogType } from '@/provider/QuickTrackingProvider'
import type { HomeSorting } from '@/types/character'
import type { SimulatorGameProject } from '@/types/simulatorGame'

type TrackEvent = (
  eventName: string,
  properties: Record<string, any> | undefined,
  logType: LogType
) => void

const TRACKING_THROTTLE_INTERVAL = 2000
const recentTrackingEvents = new Map<string, number>()

const isSimulatorTrackingDebugEnabled = () =>
  process.env.NEXT_PUBLIC_QUICK_TRACKING_DEBUG === 'true'

const getTrackingThrottleKey = (
  eventName: string,
  properties: Record<string, any>
) => `${eventName}:${JSON.stringify(properties)}`

const logSimulatorTrackingEvent = (
  status: 'send' | 'throttled',
  eventName: string,
  properties: Record<string, any>,
  logType: LogType
) => {
  if (!isSimulatorTrackingDebugEnabled()) return

  console.info('[simulator-tracking]', status, eventName, logType, properties)
}

const recordSimulatorTrackingEvent = (
  trackEvent: TrackEvent,
  eventName: string,
  properties: Record<string, any>,
  logType: LogType
) => {
  const now = Date.now()
  const throttleKey = getTrackingThrottleKey(eventName, properties)
  const lastTrackedAt = recentTrackingEvents.get(throttleKey) || 0

  if (now - lastTrackedAt < TRACKING_THROTTLE_INTERVAL) {
    logSimulatorTrackingEvent('throttled', eventName, properties, logType)
    return
  }

  recentTrackingEvents.set(throttleKey, now)
  logSimulatorTrackingEvent('send', eventName, properties, logType)
  trackEvent(eventName, properties, logType)
}

export type SimulatorCardPageName = 'discover' | 'profile' | 'time_corridor'
export type DiscoverTabType =
  | 'for_you'
  | 'trending'
  | 'worlds'
  | 'latest'
  | 'popular'
  | 'following'

export const getDiscoverTabType = (
  sorting: HomeSorting | string
): DiscoverTabType | null => {
  switch (sorting) {
    case 'For You':
      return 'for_you'
    case 'Weekly Picks':
    case 'Trending':
      return 'trending'
    case 'World':
      return 'worlds'
    case 'New Releases':
    case 'Latest':
    case 'New':
      return 'latest'
    case 'All-Time Faves':
    case 'Popular':
      return 'popular'
    case 'Following':
    case 'FollowersCharacterNew':
      return 'following'
    default:
      return null
  }
}

export const getSimulatorProjectId = (
  project: SimulatorGameProject | null | undefined
) => String(project?.game_id || project?.project_id || '')

export const getSimulatorProjectCreatorId = (
  project: SimulatorGameProject | null | undefined
) => String(project?.creator?.user_id || project?.user_id || '')

export const trackSimulatorCardExposure = (
  trackEvent: TrackEvent,
  payload: {
    simulatorId: string | number
    creatorId?: string | number | null
    pageName: SimulatorCardPageName
  }
) => {
  recordSimulatorTrackingEvent(
    trackEvent,
    'simulator_card_exposure',
    {
      simulator_id: String(payload.simulatorId),
      creator_id: String(payload.creatorId || ''),
      page_name: payload.pageName,
    },
    'EXP'
  )
}

export const trackSimulatorCardClick = (
  trackEvent: TrackEvent,
  payload: {
    simulatorId: string | number
    creatorId?: string | number | null
    pageName: SimulatorCardPageName
  }
) => {
  recordSimulatorTrackingEvent(
    trackEvent,
    'simulator_card_click',
    {
      simulator_id: String(payload.simulatorId),
      creator_id: String(payload.creatorId || ''),
      page_name: payload.pageName,
    },
    'CLK'
  )
}

export const trackSimulatorDetailExposure = (
  trackEvent: TrackEvent,
  payload: { simulatorId: string | number; creatorId?: string | number | null }
) => {
  recordSimulatorTrackingEvent(
    trackEvent,
    'simulator_detail_page_exposure',
    {
      simulator_id: String(payload.simulatorId),
      creator_id: String(payload.creatorId || ''),
    },
    'EXP'
  )
}

export const trackSimulatorDetailStartExposure = (
  trackEvent: TrackEvent,
  payload: { simulatorId: string | number; creatorId?: string | number | null }
) => {
  recordSimulatorTrackingEvent(
    trackEvent,
    'simulator_detail_start_exposure',
    {
      simulator_id: String(payload.simulatorId),
      creator_id: String(payload.creatorId || ''),
    },
    'EXP'
  )
}

export const trackSimulatorDetailStartClick = (
  trackEvent: TrackEvent,
  payload: { simulatorId: string | number; creatorId?: string | number | null }
) => {
  recordSimulatorTrackingEvent(
    trackEvent,
    'simulator_detail_start_click',
    {
      simulator_id: String(payload.simulatorId),
      creator_id: String(payload.creatorId || ''),
    },
    'CLK'
  )
}

export const trackSimulatorUsageDuration = (
  trackEvent: TrackEvent,
  payload: {
    simulatorId: string | number
    creatorId?: string | number | null
    pageDuration: number
  }
) => {
  recordSimulatorTrackingEvent(
    trackEvent,
    'simulator_usage_duration',
    {
      simulator_id: String(payload.simulatorId),
      creator_id: String(payload.creatorId || ''),
      page_duration: payload.pageDuration,
    },
    'EXP'
  )
}

export const trackDiscoverTabClick = (
  trackEvent: TrackEvent,
  tabType: DiscoverTabType
) => {
  recordSimulatorTrackingEvent(
    trackEvent,
    'discover_page_tab_click',
    {
      platform: 'Web',
      tab_type: tabType,
    },
    'CLK'
  )
}

export const trackDiscoverSubpageExposure = (
  trackEvent: TrackEvent,
  tabType: DiscoverTabType
) => {
  recordSimulatorTrackingEvent(
    trackEvent,
    'discover_subpage_exposure',
    {
      platform: 'Web',
      tab_type: tabType,
    },
    'EXP'
  )
}
